import { ChangeDetectionStrategy, Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { interval, Subscription } from 'rxjs';
import { BroadcastService } from 'src/app/core';
import { MsgConstantConfig } from 'src/app/core/config/message.constant.config';
import { MultilingualService } from 'src/app/core/modules/header/multilingual.service';
import { ClientDetailService } from 'src/app/core/services/client-detail-service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-login-header',
  templateUrl: './login-header.component.html',
  styleUrls: ['./login-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class LoginHeaderComponent implements OnInit, OnDestroy {
  @Input() syncLanguage = true;
  currentDate = '';
  currentTime = '';
  logo = '';
  selectedLanguage: any;
  selectedLanguageIcon: any;
  dropdownOpen = false;
  languages = [
    { languageValue: 'English', selectedLanguage: 'en', icon: '../assets/images/header/English.png' },
    // { languageValue: 'French', selectedLanguage: 'fr', icon: '../assets/images/header/french.png' },
    { languageValue: 'Spanish', selectedLanguage: 'es', icon: '../assets/images/header/spanish.png' }
  ];
  private eventSubscription: Subscription[] = [];
  private languageRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private router: Router,
    private clientDetailService: ClientDetailService,
    private broadCastService: BroadcastService,
    private translateService: TranslateService,
    private multilingualService: MultilingualService
  ) {
    this.logo = environment.IMG_URL + this.clientDetailService.LOGO_DARK;
    const routeEvents = this.router.events.subscribe(event => {
      const storedLanguage = localStorage.getItem('Selected_Language') ?? 'en';
      if (event instanceof NavigationEnd && storedLanguage) {
        const language = this.languages.find(lang => lang.selectedLanguage === storedLanguage);
        if (language) {
          this.selectedLanguage = language.selectedLanguage;
          this.selectedLanguageIcon = language.icon;
        }
      }
    });
    this.eventSubscription.push(routeEvents);
  }

  ngOnInit(): void {
    const timerSubscription = interval(1000).subscribe(() => {
      this.currentDate = moment().format('MM/DD/YYYY');
      this.currentTime = moment().format('h:mm A');
    });
    this.eventSubscription.push(timerSubscription);
    this.selectedLanguage = localStorage.getItem('Selected_Language') || 'en';
    const language = this.languages.find(lang => lang.selectedLanguage === this.selectedLanguage);
    if (language) {
      this.selectedLanguageIcon = language.icon;
    }
    if (!this.syncLanguage) {
      return;
    }
    this.eventSubscription.push(
      this.broadCastService.on(MsgConstantConfig.REFRESH_VIEW_LANGUAGE_ID).subscribe(() => {
        this.multilingualService.updateSelectedLanguageResponse(this.selectedLanguage);
      })
    );
    this.languageRefreshTimer = setTimeout(() => {
      this.broadCastService.broadcast(MsgConstantConfig.REFRESH_VIEW_LANGUAGE_ID);
    }, 1000);
  }

  ngOnDestroy(): void {
    if (this.languageRefreshTimer) {
      clearTimeout(this.languageRefreshTimer);
    }
    this.eventSubscription.forEach((event) => event.unsubscribe());
  }

  changeLanguage(language: any): void {
    this.selectedLanguage = language.selectedLanguage;
    this.selectedLanguageIcon = language.icon;
    localStorage.setItem('Selected_Language', this.selectedLanguage);
    this.translateService.use(this.selectedLanguage);
    this.dropdownOpen = false;
    setTimeout(() => {
      this.broadCastService.broadcast(MsgConstantConfig.REFRESH_VIEW_LANGUAGE_ID);
    }, 1000);
  }

  getSelectedLanguageValue(): string {
    const selectedLang = this.languages.find(lang => lang.selectedLanguage === this.selectedLanguage);
    return selectedLang ? selectedLang.languageValue : 'Please Select';
  }

  toggleDropdown(): void {
    this.dropdownOpen = !this.dropdownOpen;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const isInsideDropdown = target.closest('.language-dropdown');
    if (!isInsideDropdown) {
      this.dropdownOpen = false;
    }
  }
}
